/**
 * TasteInsightsSnippet
 *
 * Profile card showing TasteInsights summary with:
 * - Scroll-triggered reveal animation
 * - Rarity indicator badge with glow effects
 * - Share button integration
 * - Progress indicator for insights completeness
 */
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Skeleton,
  SkeletonText,
  Icon,
  Tooltip,
  Wrap,
  WrapItem,
  Tag,
  useBreakpointValue,
} from '@chakra-ui/react'
import { CompactActionButton } from '@/components/ui/CompactActionButton'
import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { Sparkles, ArrowUpRight, Lock, Share2 } from 'lucide-react'
import { useFeatureAccess } from '@/hooks/useFeatureAccess'
import { LockedFeature } from '@/components/subscription/LockedFeature'
import { haptics } from '@/utils/haptics'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import {
  getRarityTier,
  getRarityColors,
  getRarityDisplayName,
  shouldAnimateGlow,
} from '@/utils/rarityCalculator'
import type { TasteInsights, TasteInsightsFriend, InsightsLevel } from '@/types/tasteInsights'

const MotionBox = motion.create(Box)

interface TasteInsightsSnippetProps {
  tasteInsights: TasteInsights | TasteInsightsFriend | null | undefined
  isLoading?: boolean
  isOwnProfile: boolean
  userName?: string
  onOpenModal: () => void
  /** Callback to open share modal (only for own profile) */
  onShareClick?: () => void
}

/**
 * Get color scheme for insight level badge.
 */
function getLevelColor(level: InsightsLevel): { bg: string; color: string; hex: string } {
  switch (level) {
    case 'Walk-In':
      return { bg: 'gray.600', color: 'gray.100', hex: '#4A5568' }
    case 'Frequent Guest':
      return { bg: 'teal.500', color: 'white', hex: '#319795' }
    case 'Regular':
      return { bg: 'blue.500', color: 'white', hex: '#3182CE' }
    case 'Aficionado':
      return { bg: 'purple.500', color: 'white', hex: '#805AD5' }
    case 'VIP':
      return { bg: 'orange.500', color: 'white', hex: '#DD6B20' }
    case 'Connoisseur':
      return { bg: 'yellow.500', color: 'gray.900', hex: '#D69E2E' }
    case 'Pro':
      return { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', hex: '#667eea' }
    default:
      return { bg: 'gray.500', color: 'white', hex: '#718096' }
  }
}

/**
 * Format level name for display (capitalize, replace hyphens).
 */
function formatLevelName(level: InsightsLevel): string {
  return level
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function TasteInsightsSnippet({
  tasteInsights,
  isLoading = false,
  isOwnProfile,
  userName,
  onOpenModal,
  onShareClick,
}: TasteInsightsSnippetProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  // `LockedFeature`'s `align` is a single static value, so the responsive
  // alignment (centered on mobile, start-aligned from `md`) is resolved here,
  // as `VotingModeSelector` does for its own breakpoint. Called up here, with
  // the other hooks, because the branches below return early.
  const lockedCardAlign = useBreakpointValue<'start' | 'center'>({ base: 'center', md: 'start' }, { ssr: false }) ?? 'center'

  // Check if user can generate TasteInsights (Paire+ feature)
  const { hasAccess: canGenerateInsights } = useFeatureAccess('taste_insights_generation');

  // Animation variants - using tuple for ease to satisfy TypeScript
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0, 0, 0.58, 1] as [number, number, number, number], // easeOut cubic bezier
      },
    },
  }

  // Handle share button click
  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await haptics.spring()
    onShareClick?.()
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <Box ref={ref}>
        <Heading
          size={{ base: 'md', md: 'md' }}
          mb={{ base: 3, md: 4 }}
          textAlign={{ base: 'center', md: 'left' }}
        >
          TasteInsights™
        </Heading>
        <Box bg="var(--card-surface-2)" borderRadius="var(--r-card-compact)" p={{ base: 4, md: 5 }}>
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Skeleton height="28px" width="60px" borderRadius="var(--r-chip)" />
              <Skeleton height="24px" width="150px" />
            </HStack>
            <SkeletonText noOfLines={2} spacing={2} />
            <Skeleton height="20px" width="80px" borderRadius="full" />
          </VStack>
        </Box>
      </Box>
    )
  }

  // Empty state - no insights generated yet
  if (!tasteInsights || !tasteInsights.hasContent || !tasteInsights.isReady) {
    // For own profile: show upgrade prompt if on free tier, otherwise show quiz completion message
    const showUpgradePrompt = isOwnProfile && !canGenerateInsights;

    const emptyMessage = isOwnProfile
      ? canGenerateInsights
        ? "Discover your PersonalTaste™ - complete the Getting to Know You quiz and rate 5 restaurants to unlock your unique dining insights!"
        : "Generate your AI-powered TasteInsights™ and discover your unique dining personality!"
      : `${userName || 'This member'} hasn't unlocked their TasteInsights™ yet.`

    return (
      <MotionBox
        ref={ref}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={containerVariants}
      >
        <Heading
          size={{ base: 'md', md: 'md' }}
          mb={{ base: 3, md: 4 }}
          textAlign={{ base: 'center', md: 'left' }}
        >
          TasteInsights™
        </Heading>
        {showUpgradePrompt ? (
          // The message is the card's one piece of copy; the haptic tap runs
          // before the `tasteInsights` upgrade prompt opens.
          <LockedFeature
            variant="card"
            size="sm"
            align={lockedCardAlign}
            locked
            context="tasteInsights"
            body={emptyMessage}
            onBeforeUpgrade={haptics.spring}
          />
        ) : (
          <Box
            bg="var(--card-surface-2)"
            borderRadius="var(--r-card-compact)"
            p={{ base: 4, md: 5 }}
            borderWidth="1px"
            borderColor="var(--card-dashed)"
            borderStyle="dashed"
          >
            <VStack spacing={3} align="center">
              <HStack spacing={3} justify={{ base: 'center', md: 'flex-start' }}>
                <Icon as={isOwnProfile ? Sparkles : Lock} boxSize={5} color="purple.300" />
                <Text color="var(--card-text-3)" textAlign={{ base: 'center', md: 'left' }}>
                  {emptyMessage}
                </Text>
              </HStack>
            </VStack>
          </Box>
        )}
      </MotionBox>
    )
  }

  // Generating state
  if (tasteInsights.isGenerating) {
    return (
      <MotionBox
        ref={ref}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={containerVariants}
      >
        <Heading
          size={{ base: 'md', md: 'md' }}
          mb={{ base: 3, md: 4 }}
          textAlign={{ base: 'center', md: 'left' }}
        >
          TasteInsights™
        </Heading>
        <Box
          bg="linear-gradient(135deg, rgba(128, 90, 213, 0.3) 0%, rgba(66, 153, 225, 0.3) 100%)"
          borderRadius="var(--r-card-compact)"
          p={{ base: 4, md: 5 }}
          borderWidth="1px"
          borderColor="purple.500"
        >
          <HStack spacing={3} justify={{ base: 'center', md: 'flex-start' }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            >
              <Sparkles size={20} color="var(--chakra-colors-purple-300)" />
            </motion.div>
            <Text color="purple.200">Crafting your TasteInsights™...</Text>
          </HStack>
        </Box>
      </MotionBox>
    )
  }

  const completeness = tasteInsights.insightCompleteness
  const levelColors = getLevelColor(completeness?.level ?? 'walk-in')
  const isGradient = levelColors.bg.startsWith('linear-gradient')

  // Get rarity tier (from LLM)
  const rarityTier = getRarityTier(tasteInsights)
  const rarityColors = getRarityColors(rarityTier)
  const rarityName = getRarityDisplayName(rarityTier)
  const hasGlow = shouldAnimateGlow(rarityTier)

  // Dynamic gradient: level color → rarity color
  const cardGradient = `linear-gradient(135deg, ${levelColors.hex}66 0%, ${rarityColors.border}66 100%)`

  return (
    <MotionBox
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={containerVariants}
    >
      <HStack justify="space-between" align="center" mb={{ base: 3, md: 4 }}>
        <Heading size={{ base: 'md', md: 'md' }}>
          TasteInsights™
        </Heading>

        {/* Share button (own profile only) — paire-app#132: ProfileActionBar's
            outlined/colored treatment. */}
        {isOwnProfile && onShareClick && (
          <Tooltip label="Share your TasteInsights" placement="top">
            <Box>
              <CompactActionButton
                icon={Share2}
                tokenVar="var(--color-icon-share)"
                tint12="var(--color-icon-share-tint-12)"
                tint18="var(--color-icon-share-tint-18)"
                ariaLabel="Share TasteInsights"
                onClick={handleShareClick}
              />
            </Box>
          </Tooltip>
        )}
      </HStack>

      {/* Clickable Card */}
      <MotionBox
        as="button"
        type="button"
        width="100%"
        textAlign="left"
        bg={cardGradient}
        borderRadius="var(--r-card-compact)"
        p={{ base: 4, md: 5 }}
        borderWidth="1px"
        borderColor={rarityColors.border}
        cursor="pointer"
        position="relative"
        overflow="hidden"
        whileHover={{ y: -2, boxShadow: `0 8px 30px ${rarityColors.border}66` }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        onClick={onOpenModal}
        boxShadow={hasGlow ? `0 0 20px ${rarityColors.glow}` : undefined}
      >
        {/* Animated glow overlay for rare+ tiers */}
        {hasGlow && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            borderRadius="var(--r-card-compact)"
            pointerEvents="none"
            opacity={0.3}
            background={`radial-gradient(ellipse at center, ${rarityColors.glow} 0%, transparent 70%)`}
            animation="pulse 2s ease-in-out infinite"
            sx={{
              '@keyframes pulse': {
                '0%, 100%': { opacity: 0.2 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
        )}

        <VStack align="stretch" spacing={3} position="relative">
          {/* Tribe Header */}
          <HStack justify="space-between" align="center">
            <HStack spacing={2} flex={1} minW={0}>
              <Text fontSize={{ base: 'xl', md: '2xl' }} flexShrink={0}>{tasteInsights.tasteTribeEmojis}</Text>
              <Text
                fontSize={{ base: 'lg', md: 'xl' }}
                fontWeight="bold"
                color="white"
                noOfLines={1}
              >
                {tasteInsights.tasteTribe}
              </Text>
            </HStack>
            <Icon as={ArrowUpRight} boxSize={5} color="whiteAlpha.700" flexShrink={0} />
          </HStack>

          {/* Snippet */}
          <Text
            color="whiteAlpha.900"
            fontSize={{ base: 'sm', md: 'md' }}
            fontStyle="italic"
            noOfLines={2}
          >
            "{tasteInsights.insightSnippet}"
          </Text>

          {/* Key Flavors Tags */}
          {'insights' in tasteInsights && tasteInsights.insights?.keyFlavors && tasteInsights.insights.keyFlavors.length > 0 && (
            <Wrap spacing={2}>
              {tasteInsights.insights.keyFlavors.map((flavor, index) => (
                <WrapItem key={index}>
                  <Tag
                    size="sm"
                    bg="whiteAlpha.200"
                    color="whiteAlpha.900"
                    borderRadius="full"
                    px={3}
                    fontSize="xs"
                  >
                    {flavor}
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}

          {/* Level & Rarity Badges */}
          <HStack spacing={2}>
            <Badge
              bg={isGradient ? undefined : levelColors.bg}
              bgGradient={isGradient ? levelColors.bg : undefined}
              color={levelColors.color}
              px={2}
              py={0.5}
              borderRadius="full"
              fontSize="xs"
              textTransform="capitalize"
            >
              {formatLevelName(completeness?.level ?? 'walk-in')}
            </Badge>
            <Badge
              bg={rarityColors.bg}
              color={rarityColors.textColor}
              px={2}
              py={0.5}
              borderRadius="full"
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              boxShadow={hasGlow ? `0 0 10px ${rarityColors.glow}` : undefined}
            >
              ✨ {rarityName}
            </Badge>
          </HStack>
        </VStack>
      </MotionBox>
    </MotionBox>
  )
}
